import { z } from 'zod';
import type {
  ItemPlaceholderNode,
  LayoutTemplateDefinition,
  TemplateColumnNode,
  TemplateContainerChild,
  TemplateContainerNode,
  TemplateNode,
  TemplateRowNode,
} from '../types/layout-template';
import { LAYOUT_TEMPLATE_COUNT_MAX, LAYOUT_TEMPLATE_COUNT_MIN } from '../types/layout-template';

function countItemPlaceholders(node: TemplateNode): number {
  if (node.type === 'item') return 1;
  return node.children.reduce((sum, child) => sum + countItemPlaceholders(child), 0);
}

// Same slug ruleset as group-content / pages (Thai or lowercase Latin +
// digits, dash-separated).
const slugRx = /^[฀-๿a-z0-9]+(?:-[฀-๿a-z0-9]+)*$/u;

const slugSchema = z
  .string()
  .trim()
  .min(1, 'กรุณาใส่ slug')
  .max(80)
  .regex(slugRx, 'slug ใช้ได้: ไทย / a-z / 0-9 และ - เท่านั้น');

const nameSchema = z.string().trim().min(1, 'กรุณาใส่ชื่อ').max(120);

const descriptionSchema = z
  .string()
  .trim()
  .max(500)
  .nullable()
  .transform((v) => (v === '' || v === null ? null : v));

const nodeIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(60)
  .regex(/^[a-z0-9][a-z0-9-]*$/i, 'node id ใช้ได้แค่ a-z, 0-9, -');

const columnSpanSchema = z.number().int().min(1).max(12);
const rowGapSchema = z.number().int().min(0).max(5);

const itemPlaceholderSchema: z.ZodType<ItemPlaceholderNode> = z.object({
  id: nodeIdSchema,
  type: z.literal('item'),
  variant: z.string().trim().max(40).optional(),
});

/* ---------- Recursive tree (z.lazy + z.union — same pattern as page-layout) ---------- */

const templateContainerSchema: z.ZodType<TemplateContainerNode> = z.lazy(() =>
  z.object({
    id: nodeIdSchema,
    type: z.literal('container'),
    variant: z.enum(['fluid', 'fixed', 'carousel']),
    children: z.array(templateContainerChildSchema).max(20, 'children ไม่เกิน 20'),
  }),
);

const templateColumnSchema: z.ZodType<TemplateColumnNode> = z.lazy(() =>
  z.object({
    id: nodeIdSchema,
    type: z.literal('column'),
    span: columnSpanSchema,
    children: z.array(templateContainerChildSchema).max(20, 'children ไม่เกิน 20'),
  }),
);

const templateRowSchema: z.ZodType<TemplateRowNode> = z.lazy(() =>
  z
    .object({
      id: nodeIdSchema,
      type: z.literal('row'),
      gap: rowGapSchema,
      children: z.array(templateColumnSchema).max(12, 'row มี column ได้ไม่เกิน 12'),
    })
    .refine((row) => row.children.reduce((sum, col) => sum + col.span, 0) <= 12, {
      message: 'ผลรวม span ของ column ใน row ต้อง ≤ 12',
      path: ['children'],
    }),
);

const templateContainerChildSchema: z.ZodType<TemplateContainerChild> = z.lazy(() =>
  z.union([templateContainerSchema, templateRowSchema, itemPlaceholderSchema]),
);

function hasNonRootCarousel(node: TemplateNode, isRoot: boolean): boolean {
  if (node.type === 'item') return false;
  if (node.type === 'container' && node.variant === 'carousel' && !isRoot) return true;
  return node.children.some((c) => hasNonRootCarousel(c, false));
}

export const layoutTemplateDefinitionSchema: z.ZodType<LayoutTemplateDefinition> = z
  .object({
    version: z.literal(1),
    root: templateContainerSchema,
  })
  .superRefine((def, ctx) => {
    // Carousel is root-only: a template either is or isn't a carousel.
    // Nested carousels (carousel inside carousel, carousel deep in a column)
    // have no defined semantics and would confuse the public renderer.
    if (hasNonRootCarousel(def.root, true)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['root'],
        message: 'carousel ตั้งได้เฉพาะที่ root container เท่านั้น',
      });
    }
    // When root is carousel, its direct children must be ItemPlaceholders —
    // 1 Item = 1 slide. Rows/Columns/nested Containers don't carry meaning
    // inside a carousel and would silently disappear in the public render.
    if (def.root.variant === 'carousel') {
      const nonItem = def.root.children.find((c) => c.type !== 'item');
      if (nonItem) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['root', 'children'],
          message: `carousel container รับได้แค่ Item (เจอ ${nonItem.type})`,
        });
      }
    }
  });

/* ---------- Cross-field constraint ---------- */

function applyCountRules(
  data: {
    minCount?: number;
    maxCount?: number;
    definition?: LayoutTemplateDefinition;
  },
  ctx: z.RefinementCtx,
) {
  const { minCount, maxCount, definition } = data;

  if (minCount !== undefined && maxCount !== undefined && minCount > maxCount) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['minCount'],
      message: `minCount (${minCount}) ต้อง ≤ maxCount (${maxCount})`,
    });
  }

  if (definition !== undefined && maxCount !== undefined) {
    const itemCount = countItemPlaceholders(definition.root);
    if (itemCount > maxCount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['definition'],
        message: `จำนวน Item placeholder (${itemCount}) เกิน maxCount (${maxCount})`,
      });
    }
  }
}

/* ---------- Create / Update / List / Slug check ---------- */

export const createLayoutTemplateSchema = z
  .object({
    name: nameSchema,
    /** Auto-generated from name when omitted. */
    slug: slugSchema.optional(),
    description: descriptionSchema.optional(),
    minCount: z.coerce.number().int().min(LAYOUT_TEMPLATE_COUNT_MIN).max(LAYOUT_TEMPLATE_COUNT_MAX),
    maxCount: z.coerce.number().int().min(LAYOUT_TEMPLATE_COUNT_MIN).max(LAYOUT_TEMPLATE_COUNT_MAX),
    definition: layoutTemplateDefinitionSchema.optional(),
  })
  .superRefine((data, ctx) => applyCountRules(data, ctx));

export type CreateLayoutTemplateInput = z.infer<typeof createLayoutTemplateSchema>;

export const updateLayoutTemplateSchema = z
  .object({
    name: nameSchema.optional(),
    slug: slugSchema.optional(),
    description: descriptionSchema.optional(),
    minCount: z.coerce
      .number()
      .int()
      .min(LAYOUT_TEMPLATE_COUNT_MIN)
      .max(LAYOUT_TEMPLATE_COUNT_MAX)
      .optional(),
    maxCount: z.coerce
      .number()
      .int()
      .min(LAYOUT_TEMPLATE_COUNT_MIN)
      .max(LAYOUT_TEMPLATE_COUNT_MAX)
      .optional(),
    definition: layoutTemplateDefinitionSchema.optional(),
  })
  .superRefine((data, ctx) => applyCountRules(data, ctx));

export type UpdateLayoutTemplateInput = z.infer<typeof updateLayoutTemplateSchema>;

export const listLayoutTemplatesQuerySchema = z.object({
  q: z.string().trim().max(80).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});

export type ListLayoutTemplatesQuery = z.infer<typeof listLayoutTemplatesQuerySchema>;

export const layoutTemplateSlugCheckSchema = z.object({
  slug: slugSchema,
  excludeId: z.string().min(1).optional(),
});

export type LayoutTemplateSlugCheck = z.infer<typeof layoutTemplateSlugCheckSchema>;
